import { useAuiState } from '@assistant-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import { use } from 'react';

import { CogniteClientContext } from '#core/contexts/CogniteClientContext.ts';

import { useToolOutputAccessors } from './useToolOutputAccessors.ts';
import {
  createDatasetResolver,
  type DatasetResolver,
} from '#plugin-chat/lib/data-pipeline/resolver/dataset-resolver.ts';

/**
 * Build a {@link DatasetResolver} bound to the current thread, query
 * client, and chat-history accessors.
 *
 * Single source of truth for the hook-level composition that turns
 * React deps into a resolver. Used by {@link useDataset} / {@link useDatasets}
 * to observe producer-seeded cache entries from the UI layer, and by
 * {@link usePipelineContext} to wire a resolver into every pipeline step's
 * `PipelineContext`.
 */
export function useDatasetResolver(): DatasetResolver {
  const queryClient = useQueryClient();
  const threadId = useAuiState(({ threadListItem }) => threadListItem.id);
  const accessors = useToolOutputAccessors();
  const client = use(CogniteClientContext) ?? undefined;
  return createDatasetResolver({ queryClient, threadId, accessors, client });
}
